import useMediaQuery from "@mui/material/useMediaQuery";
import { useIsClient } from "@uidotdev/usehooks";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import BacklinkComponent from "../../../components/jobs/FooterWriteup/FooterLinks";
import HiredlyWriteup from "../../../components/jobs/FooterWriteup/HiredlyWriteup";
import SpecialisationWriteup from "../../../components/jobs/SpecialisationWriteup/SpecialisationWriteup";
import { convertIdsToUrl } from "../../../helpers/filter_management";
import { hasSGPreferencesFilled } from "../../../helpers/sg_preference_check";
import { sendTrackingEvent } from "../../../helpers/tracking_management";
import useJobApplication from "../../../hooks/useJobApplication";
import { updateRefreshDialogStatus } from "../../../redux/actions/dialog_action";
import {
  clearJobList,
  triggerFollowUpEmail,
  updateJobList,
  updateSelectedJob,
} from "../../../redux/actions/job_action";
import {
  getCountryPreferences,
  getUser,
} from "../../../redux/actions/user_action";
import { BoxContainer, ButtonText } from "../../for-you/styles";
import SharedNavbar2 from "../../shared/SharedNavbar/Navbar/Navbar";
import SharedToast from "../../shared/SharedToast/SharedToast";
import AccordionForYou from "../AccordionForYou/AccordionForYou";
import AccordionJobList from "../AccordionJobList/AccordionJobList";
import JobListNavigation from "../JobListNavigation/JobListNavigation";
import {
  FilterIcon,
  IconActionButton,
  MobileFilterWrapper,
} from "../JobSearchOptions/styles";
import { Wrapper } from "./styles";
import JLPUploadResumeFloating from "../../shared/UploadResumeFloating/wrappers/JLPUploadResumeFloating";

const CreateResumePopup = dynamic(
  () =>
    import(
      "../../profile/ResumePopups/ApplyJobResumeStatus/CreateResume/CreateResume"
    ),
  { ssr: false }
);
const RejectedResumePopup = dynamic(
  () =>
    import(
      "../../profile/ResumePopups/ApplyJobResumeStatus/RejectedResume/RejectedResume"
    ),
  { ssr: false }
);
const SharedNationalityDialog = dynamic(
  () => import("../../shared/SharedNationalityDialog/SharedNationalityDialog"),
  { ssr: false }
);
const JobAlertDialog = dynamic(
  () => import("../JobAlertDialog/JobAlertDialog"),
  { ssr: false }
);
const JobCountryPreferenceDialog = dynamic(
  () => import("../JobCountryPreferenceDialog/JobCountryPreferenceDialog"),
  { ssr: false }
);
const PostApplyDialog = dynamic(
  () => import("../PostApplyDialog/PostApplyDialog"),
  { ssr: false }
);
const RefreshDialog = dynamic(
  () => import("../../for-you/RefreshDialog/RefreshDialog"),
  { ssr: false }
);
const JobAlertsCreateDialog = dynamic(
  () => import("../../profile/JobAlertsCreateDialog/JobAlertsCreateDialog"),
  { ssr: false }
);
const JobAlertsNotificationDialog = dynamic(
  () =>
    import(
      "../../profile/JobAlertsNotificationDialog/JobAlertsNotificationDialog"
    ),
  { ssr: false }
);
const VisitorJobAlertDialog = dynamic(
  () => import("../VisitorJobAlertDialog/VisitorJobAlertDialog"),
  { ssr: false }
);
const VisitorJobAlertSuccesDialog = dynamic(
  () =>
    import(
      "../VisitorJobAlertDialog/VisitorJobAlertSuccessDialog/VisitorJobAlertSuccessDialog"
    ),
  { ssr: false }
);

const JobFollowUpEmailDialog = dynamic(
  () => import("../JobFollowUpEmailDialog/JobFollowUpEmailDialog"),
  { ssr: false }
);

const SGPreferenceDialog = dynamic(
  () => import("../../onboarding/SGPreferenceDialog/SGPreferenceDialog"),
  { ssr: false }
);

export default function JobListWrapper(props) {
  const {
    serverJobs,
    ssrFilterParams,
    ssrPageParams,
    currentTab,
    cmsSpecialisations,
    cmsLocations,
    companyWriteup,
  } = props;

  const dispatch = useDispatch();

  const isClient = useIsClient();
  const isDesktop = useMediaQuery("(min-width: 1280px)");
  const isMobile = useMediaQuery("(max-width: 640px)");

  const [selectedTab, setSelectedTab] = useState(currentTab);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [viewedCards, setViewedCards] = useState(new Set());

  const [dialogOpen, setDialogOpen] = useState(true);
  const [sgPreferenceDialogOpen, setSgPreferenceDialogOpen] = useState(false);
  const [hasShownSgDialog, setHasShownSgDialog] = useState(false);
  const [hasFetchedCountryPrefs, setHasFetchedCountryPrefs] = useState(false);

  const jobSelectedState = selectedJobs.length > 0;
  const jobSearchRef = useRef(null);
  const mobileFilterAnchorRef = useRef(null);

  const alertBanner = useSelector((state) => state.dialog.showAlertBanner);
  const specialisationList = useSelector((state) => state.jobs?.specialisation);
  const stateList = useSelector((state) => state.jobs?.stateRegions);
  const refreshDialog = useSelector(
    (state) => state?.dialog?.showRefreshDialog
  );
  const forYouJobs = useSelector((state) => state.jobs?.forYouJobs);
  const countryPreference = useSelector(
    (state) => state.user.profileFields.countryPreference
  );
  const fetchingCountryPreference = useSelector(
    (state) => state.user.fetchingCountryPreference
  );

  const toastQueue = useRef([]);
  const jobListRef = useRef(null);

  const trackCurrentPage = (jobTypes, currentPage, jobsPerPage) => {
    const startIndex = currentPage * jobsPerPage;
    return jobTypes?.slice(startIndex, startIndex + jobsPerPage);
  };

  const filteredJobs = trackCurrentPage(forYouJobs, currentPage, 5);

  const jobOrigin = "job_list";
  const jobDiscovery = "search";

  const user = getUser();
  const router = useRouter();

  const { token } = router.query;
  const [openFollowUpPopup, setOpenFollowUpPopup] = useState(false);

  useEffect(() => {
    if (!token) return;
    const fetchFollowUp = async () => {
      const result = await triggerFollowUpEmail({ token: token });

      if (result?.error) {
        router.replace("/jobs", undefined, { shallow: true });
      } else {
        setOpenFollowUpPopup(true);
      }
    };

    fetchFollowUp();
  }, [token]);

  const handleCloseFollowUpPopup = () => {
    setOpenFollowUpPopup(false);
    router.replace("/jobs", undefined, { shallow: true });
  };

  // Fetch country preferences when user is logged in (only once)
  useEffect(() => {
    if (isClient && user && !hasFetchedCountryPrefs) {
      dispatch(getCountryPreferences());
      setHasFetchedCountryPrefs(true);
    }
  }, [isClient, user, hasFetchedCountryPrefs, dispatch]);

  // Check if SG Preference Dialog should be shown
  useEffect(() => {
    // Only check when:
    // 1. User is logged in
    // 2. Selected tab is "job-list"
    // 3. Client has loaded
    if (!isClient || !user || selectedTab !== "job-list") {
      setSgPreferenceDialogOpen(false);
      return;
    }

    // If dialog was already shown in this session, don't re-trigger it
    if (hasShownSgDialog) {
      return;
    }

    // Avoid checking until country preferences have been fetched
    if (fetchingCountryPreference) {
      return;
    }

    const hasLoadedCountryPreference =
      countryPreference && Object.keys(countryPreference).length > 0;

    if (!hasLoadedCountryPreference) {
      return;
    }

    // Check if user has filled Singapore preferences
    const hasFilled = hasSGPreferencesFilled(countryPreference);

    // Show dialog if preferences are not filled
    if (!hasFilled) {
      setSgPreferenceDialogOpen(true);
      setHasShownSgDialog(true); // Mark as shown
    } else {
      setSgPreferenceDialogOpen(false);
    }
  }, [
    isClient,
    user,
    selectedTab,
    countryPreference,
    hasShownSgDialog,
    fetchingCountryPreference,
  ]);

  const handleCloseSgPreferenceDialog = () => {
    setSgPreferenceDialogOpen(false);
  };

  const {
    applyJobFunc,
    runWithConnorsApplyConfirm,
    checkLoggedIn,
    checkNationalityPhoneNumberLocation,
    checkRightToWorkAndRelocate,
    checkResumeRejected,
    checkScrapedJob,
  } = useJobApplication(jobOrigin, jobDiscovery);

  function triggerToast(toastMessage, state) {
    toast(() => toastMessage, {
      className: state ? "Toast-success" : "Toast-error",
    });
  }

  function handleTabNavClick(tab) {
    if (tab === selectedTab) {
      return;
    }

    setSelectedTab(tab);
    dispatch(clearJobList());

    if (
      tab === "job-list" &&
      (Object.keys(ssrFilterParams).length > 0 || ssrPageParams)
    ) {
      const { path, query } = convertIdsToUrl(
        ssrFilterParams,
        specialisationList,
        stateList
      );

      router.push({ pathname: path, query }, undefined, {
        shallow: false,
      });
    }
  }

  async function executeApplyJobClick(e, job) {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    dispatch(updateSelectedJob(job));
    // NOT LOGGED IN
    if (checkLoggedIn(job)) {
      sendTrackingEvent({
        event: "applied-job",
        "job-title": job.title,
        resume: false,
        logged_in: false,
        scraped: job.scraped !== "",
        "job-bucket-id": null,
        "job-id": job?.id,
      });
      return;
    }
    // CONDITION - NO NATIONALITY OR MOBILE NUMBER OR CURRENT LOCATION
    if (checkNationalityPhoneNumberLocation(job)) {
      return;
    }

    // CONDITION - RIGHT TO WORK OR RELOCATION PREFERENCE
    if (checkRightToWorkAndRelocate(job)) {
      return;
    }

    // CONDITION - NO RESUME / RESUME REJECTED
    // Do not send in application if user has no resume/rejected
    if (checkResumeRejected(job)) {
      sendTrackingEvent({
        event: "applied-job",
        "job-title": job.title,
        resume: false,
        logged_in: true,
        scraped: job.scraped !== "",
        "job-bucket-id": null,
        "job-id": job?.id,
      });
      return;
    }

    const applySourceCE = "CE_job-apply";

    // CONDITION - SCRAPED JOB
    if (checkScrapedJob(job)) {
      sendTrackingEvent({
        event: applySourceCE,
        origin: "job_list",
        job_discovery: jobDiscovery,
        "job-title": job.title,
        "job-id": job?.id,
        resume: false,
        logged_in: true,
        scraped: job.scraped !== "",
        job_preference_toggle: "off",
        "job-bucket-id": null,
      });
      return;
    }

    const params = {
      jobId: job.id,
      canCancel: false,
      origin: jobOrigin,
      jobDiscovery: jobDiscovery,
      applySource: "jlp_apply_now",
    };

    setDialogOpen(true);
    const response = await applyJobFunc(job, params);

    if (response.success) {
      sendTrackingEvent({
        event: applySourceCE,
        origin: "job_list",
        job_discovery: jobDiscovery,
        "job-title": job.title,
        "job-id": job?.id,
        resume: true,
        logged_in: true,
        scraped: job.scraped !== "",
        job_preference_toggle: "off",
        "job-bucket-id": response?.payload?.jobBucketId ?? null,
      });
    }
    dispatch(updateSelectedJob({}));
  }

  function onApplyJobClick(e, job) {
    runWithConnorsApplyConfirm(job, () => executeApplyJobClick(e, job), e);
  }

  function actionButton({ closeToast, data }) {
    function handleTriggerAction() {
      data.action();
      closeToast(true);
    }

    return (
      <BoxContainer justifyContent={"space-between"} alignItems={"center"}>
        <span>Jobs refreshed</span>
        <ButtonText onClick={handleTriggerAction}>Undo</ButtonText>
      </BoxContainer>
    );
  }

  function handleUndoRefresh() {
    // create a generate function to perform undo action based on its params
    const handleUndo = (currentPage, setPage, setLoading, setSelectedJobs) => {
      if (currentPage < 0) {
        return;
      }

      setPage(currentPage);
      setLoading(true);
      setSelectedJobs([]);

      if (toastQueue.current.length >= 2) {
        const oldestToast = toastQueue.current.shift();
        if (oldestToast) toast.dismiss(oldestToast);
      }

      const newToast = toast.success("Jobs reverted.", { timer: 5000 });
      toastQueue.current.push(newToast);

      setTimeout(() => {
        setLoading(false);
      }, 300);
    };

    handleUndo(currentPage, setCurrentPage, setRefreshLoading, setSelectedJobs);
  }

  function handleRefreshJobs(fromDialog = false) {
    if (shouldShowRefreshDialog(fromDialog)) {
      dispatch(updateRefreshDialogStatus(true));
      return;
    }

    refreshForYouJobs();
  }

  function shouldShowRefreshDialog(fromDialog) {
    return !refreshDialog && dialogOpen;
  }

  function refreshForYouJobs() {
    if (forYouJobs?.length > 0) {
      const nextPage = (currentPage + 1) % Math.ceil(forYouJobs.length / 5);
      if (nextPage === 0) {
        router.reload();
        return;
      }
      resetForYouJobsScroll();
      updateJobListPage(
        nextPage,
        setCurrentPage,
        setRefreshLoading,
        setSelectedJobs
      );
    }
  }

  // Hide FYP job
  function handleHideJob(job) {
    const updatedJobs = forYouJobs.filter((j) => j.id !== job.id);
    const jobsPerPage = 5;
    let newPage = currentPage;
    if (
      updatedJobs.length > 0 &&
      updatedJobs.length <= currentPage * jobsPerPage
    ) {
      newPage = Math.max(currentPage - 1, 0);
    }
    dispatch(updateJobList(updatedJobs));
    setCurrentPage(newPage);
  }

  function updateJobListPage(nextPage, setPage, setLoading, setSelectedJobs) {
    setPage(nextPage);
    setLoading(true);
    setSelectedJobs([]);

    if (toastQueue.current.length >= 2) {
      const oldestToast = toastQueue.current.shift();
      if (oldestToast) toast.dismiss(oldestToast);
    }

    const newToast = toast.success(actionButton, {
      autoClose: 8000,
      data: {
        action: handleUndoRefresh,
      },
    });

    toastQueue.current.push(newToast);

    setTimeout(() => {
      setLoading(false);
    }, 300);
  }

  function resetForYouJobsScroll() {
    if (jobListRef.current) {
      jobListRef.current.style.scrollBehavior = "auto";
      jobListRef.current.scrollLeft = 0;
      setTimeout(() => {
        if (jobListRef.current) {
          jobListRef.current.style.scrollBehavior = "smooth";
        }
      }, 0);
    }
  }

  return (
    <Fragment>
      <SharedNavbar2 
        page={"job-list-page"} 
        breadcrumbItems={[{ label: "nav.jobs" }]}
      />
      <>
        <Wrapper selectedTab={selectedTab}>
          <JobListNavigation
            selectedTab={selectedTab}
            handleTabNavClick={handleTabNavClick}
            jobSearchRef={jobSearchRef}
            filterAnchorRef={mobileFilterAnchorRef}
          />
          {isMobile && selectedTab === "job-list" ? (
            <MobileFilterWrapper>
              <IconActionButton
                aria-label="Open filters"
                onClick={() => jobSearchRef.current?.openFilter?.()}
                ref={mobileFilterAnchorRef}
                $active={false}
              >
                <FilterIcon />
              </IconActionButton>
            </MobileFilterWrapper>
          ) : null}
          {/* create dedicate space for ssr loading for prevent layout shift */}
          {!isClient ? <div style={{ marginTop: "80px" }}></div> : null}
          {selectedTab === "for-you" ? (
            <AccordionForYou
              jobListRef={jobListRef}
              jobs={forYouJobs}
              jobListTab={selectedTab}
              handleTabChange={handleTabNavClick}
              handleRefreshJobs={handleRefreshJobs}
              handleUndoRefresh={handleUndoRefresh}
              selectedJobs={selectedJobs}
              setSelectedJobs={setSelectedJobs}
              setViewedRecommendedJobsCards={setViewedCards}
              filteredJobs={filteredJobs}
              setViewedCards={setViewedCards}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              refreshLoading={refreshLoading}
              setDialogOpen={setDialogOpen}
              handleHideJob={handleHideJob}
            />
          ) : null}
          {selectedTab === "job-list" ? (
            <AccordionJobList
              serverJobs={serverJobs}
              ssrFilterParams={ssrFilterParams}
              ssrPageParams={ssrPageParams}
              onApplyJobClick={onApplyJobClick}
            />
          ) : null}
        </Wrapper>
        {selectedTab === "job-list" && (
          <>
            <BacklinkComponent
              cmsSpecialisations={cmsSpecialisations}
              cmsLocations={cmsLocations}
            />
            <SpecialisationWriteup
              specialisationList={specialisationList}
              ssrFilterParams={ssrFilterParams}
              cmsSpecialisations={cmsSpecialisations}
            />
            <HiredlyWriteup companyWriteup={companyWriteup} />
          </>
        )}
      </>

      {/* Popup components */}
      {isClient ? (
        <>
          <SharedToast />
          <CreateResumePopup />
          <RejectedResumePopup />
          <PostApplyDialog
            jobListRef={jobListRef}
            page={currentTab}
            selectedTab={selectedTab}
            handleTabChange={handleTabNavClick}
            setDialogOpen={setDialogOpen}
          />
          <RefreshDialog
            isDesktop={isDesktop}
            jobs={filteredJobs}
            viewedCards={viewedCards}
            handleRefreshJobs={handleRefreshJobs}
            jobSelectedState={jobSelectedState}
            dialogOpen={dialogOpen}
            setDialogOpen={setDialogOpen}
          />
          <JobAlertDialog />
          <SharedNationalityDialog jobOrigin={"job_list"} />
          <JobCountryPreferenceDialog
            onApplyJobClicked={onApplyJobClick}
            useLocalApply={true}
            origin={"job_list"}
          />
          <JobAlertsCreateDialog
            selectedCountry={"my"}
            triggerToast={triggerToast}
          />
          <JobAlertsNotificationDialog selectedCountry={"my"} />
          <JobFollowUpEmailDialog
            open={openFollowUpPopup}
            onHandleClose={handleCloseFollowUpPopup}
          />
          <VisitorJobAlertDialog />
          <VisitorJobAlertSuccesDialog />
          {selectedTab === "job-list" && (
            <SGPreferenceDialog
              open={sgPreferenceDialogOpen}
              onClose={handleCloseSgPreferenceDialog}
            />
          )}
        </>
      ) : null}
      <JLPUploadResumeFloating
        signupSource={
          selectedTab === "for-you"
            ? "jlp_fyp_floating_resume"
            : "jlp_floating_resume"
        }
      />
    </Fragment>
  );
}
